import React, { useEffect, useState } from "react";
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchItCategory, fetchItType, addItCategoryCriteriaValue } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function ItCategoryClassificationCriteriaAddPage() {
  const { businessDomainId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [criterias, setCriterias] = useState([]);
  const [criteriaValues, setCriteriaValues] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [allValues, setAllValues] = useState([]);

  const basePath = `/strategy/${businessDomainId}/it-categories/${categoryId}`;

  useEffect(() => {
    (async () => {
      try {
        const cat = await fetchItCategory(categoryId);
        const itTypeId = cat.itTypeId;
        if (!itTypeId) return;
        const it = await fetchItType(itTypeId);
        setCriterias(it.classificationCriterias || []);
        setAllValues(it.classificationCriteriaValues || []);
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const handleCriteriaChange = (e) => {
    const name = e.target.value;
    setSelectedCriteria(name);
    setSelectedValue("");
    setCriteriaValues(
      allValues.filter((v) => v.classificationCriteria?.classificationCriteriaName === name)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCriteria || !selectedValue) {
      notify.error(t("itCategory.classificationCriteria.selectRequired"));
      return;
    }
    try {
      const value = criteriaValues.find((v) => v.classificationCriteriaValueName === selectedValue);
      if (!value) return;
      const res = await addItCategoryCriteriaValue(categoryId, value);
      if (res?.result === "NG") {
        notify.error(res.errorMessage || t("message.create.error"));
        return;
      }
      notify.success(t("message.create.success"));
      navigate(`${basePath}/classification-criteria`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("itCategory.classificationCriteriaValues")}：{t("common.add")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 480 }}>
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>{t("itCategory.classificationCriteriaName")}</InputLabel>
          <Select value={selectedCriteria} onChange={handleCriteriaChange}
            label={t("itCategory.classificationCriteriaName")}>
            {criterias.map((c) => (
              <MenuItem key={c.classificationCriteriaName} value={c.classificationCriteriaName}>
                {c.classificationCriteriaName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth required sx={{ mb: 3 }} disabled={!selectedCriteria}>
          <InputLabel>{t("itCategory.classificationCriteriaValueName")}</InputLabel>
          <Select value={selectedValue} onChange={(e) => setSelectedValue(e.target.value)}
            label={t("itCategory.classificationCriteriaValueName")}>
            {criteriaValues.map((v) => (
              <MenuItem key={v.classificationCriteriaValueName} value={v.classificationCriteriaValueName}>
                {v.classificationCriteriaValueName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(`${basePath}/classification-criteria`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
